import { StrictMode, Suspense, lazy, type ComponentType } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './styles/tokens.css';
import { I18nProvider } from './lib/i18n.js';
import { AuthProvider } from './lib/auth.js';
import { AppChrome } from './components/AppChrome.js';

// Route-level code splitting (§perf). Everything below the app shell loads as its
// own chunk, so a guest opening /e/:code no longer downloads the host console,
// admin, payment, marketing, etc. up front — that eager monolith was a big part
// of the slow first paint on mobile. Pages use named exports, hence the mapping.
const lazyPage = <K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) =>
  lazy(() => load().then((m) => ({ default: m[name] })));

const HomePage = lazyPage(() => import('./pages/HomePage.js'), 'HomePage');
const ComingSoonPage = lazyPage(() => import('./pages/ComingSoonPage.js'), 'ComingSoonPage');
const MarketingPage = lazyPage(() => import('./pages/MarketingPage.js'), 'MarketingPage');
const HowItWorksPage = lazyPage(() => import('./pages/HowItWorksPage.js'), 'HowItWorksPage');
const AdminPage = lazyPage(() => import('./pages/AdminPage.js'), 'AdminPage');
const JoinPage = lazyPage(() => import('./pages/JoinPage.js'), 'JoinPage');
const ResultsPage = lazyPage(() => import('./pages/ResultsPage.js'), 'ResultsPage');
const HostPage = lazyPage(() => import('./pages/HostPage.js'), 'HostPage');
const CreateEventPage = lazyPage(() => import('./pages/CreateEventPage.js'), 'CreateEventPage');
const HostEventPage = lazyPage(() => import('./pages/HostEventPage.js'), 'HostEventPage');
const InvitationPage = lazyPage(() => import('./pages/InvitationPage.js'), 'InvitationPage');
// LegalPage takes a `kind` prop, so lazy it directly to preserve its typing.
const LegalPage = lazy(() => import('./pages/LegalPage.js').then((m) => ({ default: m.LegalPage })));
const ForgotPasswordPage = lazyPage(() => import('./pages/ForgotPasswordPage.js'), 'ForgotPasswordPage');
const ResetPasswordPage = lazyPage(() => import('./pages/ResetPasswordPage.js'), 'ResetPasswordPage');
const PublishedPage = lazyPage(() => import('./pages/PublishedPage.js'), 'PublishedPage');
const PaymentPage = lazyPage(() => import('./pages/PaymentPage.js'), 'PaymentPage');
const WallPage = lazyPage(() => import('./pages/WallPage.js'), 'WallPage');
const UnsubscribePage = lazyPage(() => import('./pages/UnsubscribePage.js'), 'UnsubscribePage');
const DeleteAccountPage = lazyPage(() => import('./pages/DeleteAccountPage.js'), 'DeleteAccountPage');

function RouteFallback() {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
      <div className="pp-spinner" />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <AuthProvider>
          <AppChrome />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Public root: the marketing landing that explains + promotes the
                  product. The working app lives at its own paths below
                  (/event_creation, /host, /e/:joinCode, ...); the old join/host
                  landing stays reachable at /app. */}
              <Route path="/" element={<MarketingPage />} />
              {/* Host-facing docs, linked from the public header. */}
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/app" element={<HomePage />} />
              {/* Guest entry from the QR code: fiesta.app/e/:joinCode (§6B screen 8). */}
              <Route path="/e/:joinCode" element={<JoinPage />} />
              {/* No-login projector Wall — cast to a TV/screen at the party (§5.3). */}
              <Route path="/wall/:joinCode" element={<WallPage />} />
              {/* Gated post-event results (§5.6 / screen 18). */}
              <Route path="/results/:eventId" element={<ResultsPage />} />
              {/* Host console (§6B). /event_creation is the public-facing entry alias. */}
              <Route path="/host" element={<HostPage />} />
              <Route path="/event_creation" element={<HostPage />} />
              {/* Password reset (§11.6). */}
              <Route path="/forgot" element={<ForgotPasswordPage />} />
              <Route path="/reset" element={<ResetPasswordPage />} />
              <Route path="/host/new" element={<CreateEventPage />} />
              <Route path="/host/e/:id" element={<HostEventPage />} />
              <Route path="/host/e/:id/invitation" element={<InvitationPage />} />
              <Route path="/host/e/:id/pay" element={<PaymentPage />} />
              <Route path="/host/e/:id/published" element={<PublishedPage />} />
              {/* Superadmin console. */}
              <Route path="/admin" element={<AdminPage />} />
              {/* Legal (§11.5). */}
              <Route path="/privacy" element={<LegalPage kind="privacy" />} />
              <Route path="/terms" element={<LegalPage kind="terms" />} />
              {/* No-login marketing unsubscribe / data-erasure (§11.5). */}
              <Route path="/unsubscribe" element={<UnsubscribePage />} />
              <Route path="/delete-account" element={<DeleteAccountPage />} />
              <Route path="*" element={<ComingSoonPage />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
